<template>
  <div class="h-full flex relative flex-col gap-2 min-h-screen">
    <div class="flex gap-2">
      <BaseTabs v-model="activeTab" :tabs="[tabs.without, tabs.with]" />

      <button class="ml-auto btn btn-outline-primary" @click="exportFile()">
        Exportera
      </button>

      <button class="btn btn-primary" @click="create">
        Nytt Kassaregister
      </button>
    </div>

    <div class="grid grid-cols-2 gap-2">
      <div class="flex gap-3 items-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRow"
          :per-page="perPage"
        />
        <span> {{ currentPageInfo }}</span>
      </div>

      <BaseInput v-model="searchQuery" placeholder="Sök" />
    </div>

    <div v-if="error" class="error apollo">
      Ett fel inträffade
    </div>

    <transition name="fade">
      <div v-show="loading" class="absolute inset-0 grid place-items-center">
        <b-spinner class="align-middle" />
      </div>
    </transition>

    <div class="flex-grow">
      <b-table
        hover
        striped
        show-empty
        empty-text="Inga kassaregister"
        :fields="fields"
        :items="
          result && result.cashregisters ? result.cashregisters.items : []
        "
        @row-clicked="details"
      />
    </div>

    <div class="flex gap-3 items-center">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRow"
        :per-page="perPage"
      />
      <span> {{ currentPageInfo }}</span>
    </div>

    <modal
      name="create-cashregister"
      transition="pop-out"
      :pivot-y="0.2"
      height="auto"
      adaptive
      @before-open="beforeCreateCashRegisterModalOpen"
    >
      <div class="p-3">
        <BaseSelect
          v-model="form.companyId"
          label="Företag"
          :options="companies"
        />

        <BaseSelect
          v-model="form.saloonId"
          :disabled="!form.companyId || isLoadingSalons"
          label="Salong"
          class="mt-3"
          :options="saloons"
        />
        <p v-if="isLoadingSalons || isLoadingCompanies">Laddar ...</p>

        <div class="mt-5 text-right">
          <button
            class="btn btn-default"
            @click="$modal.hide('create-cashregister')"
          >
            Avbryt
          </button>
          <button
            class="btn btn-primary"
            :disabled="!form.saloonId || !form.companyId || createCashLoading"
            @click="handleSubmit"
          >
            Spara
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { useQuery, useResult, useMutation } from '@vue/apollo-composable'
import { useDownloadFile } from '@/composables/use-downloadfile'
import {
  ref,
  reactive,
  watch,
  computed,
  watchEffect,
} from '@vue/composition-api'
import GET_CASHREGISTERS from '@/graphql/Cashregisters.gql'
import GET_SALOONS from '@/graphql/Saloons.gql'
import gql from 'graphql-tag'
import BaseTabs from '@/components/BaseTabs.vue'

export default {
  components: { BaseTabs },
  setup(props, { root: { $router, $modal } }) {
    const getRoute = () => $router.currentRoute

    const form = reactive({
      saloonId: '',
      companyId: '',
    })
    const tabs = {
      without: 'Utan aktiveringar',
      with: 'Med aktiveringar',
    }
    const activeTab = ref(getRoute().query.tab || tabs.without)
    const searchQuery = ref(getRoute().query.q || '')
    watchEffect(() => {
      const currentRoute = getRoute()
      const newQueryObject = {
        ...currentRoute.query,
        q: searchQuery.value,
        tab: activeTab.value,
      }
      if (
        Object.keys(newQueryObject).some(
          key => (newQueryObject[key] || '') !== (currentRoute.query[key] || '')
        )
      ) {
        $router.replace({ ...currentRoute, query: newQueryObject })
      }
    })
    watchEffect(() => {
      const { query } = getRoute()
      if (query.tab && query.tab !== activeTab.value) {
        activeTab.value = query.tab
      }
      if (query.q && query.q !== searchQuery.value) {
        searchQuery.value = query.q
      }
    })

    const currentPage = ref(1)
    const perPage = ref(20)
    const totalRow = ref(0)
    const querySearchString = ref(searchQuery.value)

    const queryArgs = computed(() => ({
      search: querySearchString.value,
      skip: (currentPage.value - 1) * perPage.value,
      take: perPage.value,
      hasActivations: activeTab.value === tabs.with,
    }))

    const { result, onResult, loading, error, refetch } = useQuery(
      GET_CASHREGISTERS,
      () => ({ where: queryArgs.value }),
      {
        fetchPolicy: 'cache-first',
      }
    )

    onResult(({ data }) => {
      if (data) {
        totalRow.value = data.cashregisters.totalCount
      }
    })

    const currentPageInfo = computed(() => {
      const startRow = (currentPage.value - 1) * perPage.value + 1
      const endRow = Math.min(currentPage.value * perPage.value, totalRow.value)
      const totalPage = Math.ceil(totalRow.value / perPage.value)

      return `sida ${currentPage.value}/${totalPage} visar ${startRow}-${endRow}`
    })

    const createCashRegisterModalIsOpen = ref(false)
    function beforeCreateCashRegisterModalOpen() {
      createCashRegisterModalIsOpen.value = true
    }
    const { result: companiesResult, loading: isLoadingCompanies } = useQuery(
      gql`
        query companyIdsAndNames {
          companyIdsAndNames {
            id
            name
          }
        }
      `,
      null,
      () => ({
        fetchPolicy: 'cache-first',
        enabled: createCashRegisterModalIsOpen.value,
      })
    )

    const companies = computed(() => {
      return companiesResult && companiesResult.value
        ? companiesResult.value.companyIdsAndNames
        : []
    })

    const { result: saloonsResult, loading: isLoadingSalons } = useQuery(
      GET_SALOONS,
      () => ({
        where: {
          companyId: form.companyId,
        },
      }),
      () => ({
        enabled: !!form.companyId,
      })
    )

    const saloonResult = useResult(saloonsResult, [], data => data.saloons)
    const saloons = computed(() => {
      return saloonResult.value.filter(
        s => s.company && !s.company.parentCompany
      )
    })

    watch(
      () => form.companyId,
      () => {
        form.saloonId = ''
      }
    )

    const {
      mutate: createCashRegister,
      onDone,
      loading: createCashLoading,
    } = useMutation(require('../../graphql/CreateCashregister.gql'), () => ({
      variables: {
        data: {
          saloonId: form.saloonId,
          companyId: form.companyId,
        },
      },
    }))

    onDone(result => {
      $modal.hide('create-cashregister')
      activeTab.value = tabs.without
      refetch()
      form.companyId = ''
      form.saloonId = ''
    })

    async function handleSubmit() {
      createCashRegister()
    }

    const { download } = useDownloadFile()
    async function exportFile() {
      download('/cashregisters/export', 'Kassaregister_export.xlsx')
    }

    const debounce = ref(null)
    watch(
      () => searchQuery.value,
      value => {
        clearTimeout(debounce.value)
        debounce.value = setTimeout(() => {
          querySearchString.value = value.trim()
          currentPage.value = 1
        }, 500)
      }
    )

    function details(row) {
      $router.push({
        name: 'cashregisterdetails',
        params: { id: row.id },
        query: { redirect: getRoute().fullPath },
      })
    }
    function create() {
      $modal.show('create-cashregister')
    }

    const fields = [
      {
        key: 'companyName',
        label: 'Namn',
      },
      {
        key: 'vat',
        label: 'Orgnr',
      },
      {
        key: 'name',
        label: 'Kassanr',
      },
      {
        key: 'productionNumber',
        label: 'Tillverkningsnummer',
      },
      {
        key: 'street',
        label: 'Gata',
      },
      {
        key: 'city',
        label: 'Stad',
      },
      {
        key: 'zipcode',
        label: 'Postnummer',
      },
    ]

    return {
      activeTab,
      beforeCreateCashRegisterModalOpen,
      companies,
      create,
      createCashLoading,
      currentPage,
      currentPageInfo,
      details,
      error,
      exportFile,
      fields,
      form,
      handleSubmit,
      isLoadingCompanies,
      isLoadingSalons,
      loading,
      perPage,
      result,
      saloons,
      searchQuery,
      tabs,
      totalRow,
    }
  },
}
</script>

<style>
@keyframes fade-in {
  to {
    opacity: 1;
  }
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
.fade-enter-active {
  transition: opacity 0.3s;
}
</style>
